<script>
import { mapGetters } from 'vuex';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import { REPORTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useAlert, useTrack } from 'dashboard/composables';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import FilterSelector from './components/FilterSelector.vue';

export default {
  name: 'AgentsInboxesReports',
  components: {
    FilterSelector,
    UserAvatarWithName,
  },
  data() {
    return {
      from: null,
      to: null,
      conversationDirection: 'all',
      selectedAgents: [],
      selectedInboxes: [],
      hasHorizontalOverflow: false,
      isScrolledToEnd: false,
      isDownloading: false,
    };
  },
  computed: {
    ...mapGetters({
      report: 'getAgentsInboxesReport',
      reportUiFlags: 'getAgentsInboxesReportUIFlags',
    }),
    inboxes() {
      return this.report.inboxes || [];
    },
    rows() {
      return this.report.rows || [];
    },
    hasRows() {
      return this.rows.length > 0 && this.inboxes.length > 0;
    },
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        conversationDirection: this.conversationDirection,
        agentIds: this.selectedAgents.map(agent => agent.id),
        inboxIds: this.selectedInboxes.map(inbox => inbox.id),
      };
    },
    isFetching() {
      return this.reportUiFlags.isFetching;
    },
    isDownloadDisabled() {
      return this.isFetching || this.isDownloading || !this.from || !this.to;
    },
    columnTotals() {
      return this.inboxes.reduce((result, inbox) => {
        result[inbox.id] = this.rows.reduce(
          (sum, row) => sum + this.cellValue(row, inbox.id),
          0
        );
        return result;
      }, {});
    },
    grandTotal() {
      return this.rows.reduce((sum, row) => sum + (row.total || 0), 0);
    },
  },
  mounted() {
    window.addEventListener('resize', this.updateHorizontalScrollState);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateHorizontalScrollState);
  },
  methods: {
    loadReports() {
      if (!this.from || !this.to) {
        return null;
      }

      return this.$store
        .dispatch('fetchAgentsInboxesReport', this.requestPayload)
        .then(() => {
          this.$nextTick(this.updateHorizontalScrollState);
        })
        .catch(() => {
          useAlert(this.$t('AGENTS_INBOXES_REPORTS.DATA_FETCHING_FAILED'));
        });
    },
    downloadReports() {
      if (this.isDownloadDisabled) {
        return null;
      }

      this.isDownloading = true;
      return this.$store
        .dispatch('downloadAgentsInboxesReports', {
          ...this.requestPayload,
          fileName: generateFileName({ type: 'agents-inboxes', to: this.to }),
        })
        .finally(() => {
          this.isDownloading = false;
        });
    },
    onFilterChange({
      from,
      to,
      selectedConversationDirection,
      selectedAgents,
      selectedInbox,
    }) {
      this.from = from;
      this.to = to;
      this.conversationDirection = selectedConversationDirection?.id || 'all';
      this.selectedAgents = selectedAgents || [];
      this.selectedInboxes = Array.isArray(selectedInbox) ? selectedInbox : [];
      this.loadReports();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: {
          from,
          to,
          conversationDirection: this.conversationDirection,
          agentIds: this.requestPayload.agentIds,
          inboxIds: this.requestPayload.inboxIds,
        },
        reportType: 'agents_inboxes',
      });
    },
    cellValue(row, inboxId) {
      return row.inboxes?.[inboxId] ?? row.inboxes?.[String(inboxId)] ?? 0;
    },
    agentCellUser(agent) {
      return {
        name: agent.name,
        thumbnail: agent.thumbnail,
        email: agent.email,
      };
    },
    updateHorizontalScrollState() {
      const tableScroller = this.$refs.tableScroller;
      if (!tableScroller) {
        return;
      }

      this.hasHorizontalOverflow =
        tableScroller.scrollWidth > tableScroller.clientWidth;
      this.isScrolledToEnd =
        tableScroller.scrollLeft + tableScroller.clientWidth >=
        tableScroller.scrollWidth - 1;
    },
  },
};
</script>

<template>
  <div class="flex-1 p-4 overflow-auto">
    <div class="flex flex-col gap-3 md:flex-row md:items-start">
      <div class="flex-1">
        <FilterSelector
          show-agents-filter
          show-inbox-filter
          multiple-inbox-filter
          :show-business-hours-switch="false"
          @filter-change="onFilterChange"
        />
      </div>
      <woot-button
        icon="arrow-download"
        color-scheme="success"
        class-names="flex-shrink-0"
        :disabled="isDownloadDisabled"
        @click="downloadReports"
      >
        {{ $t('AGENTS_INBOXES_REPORTS.DOWNLOAD') }}
      </woot-button>
    </div>

    <section
      class="overflow-hidden bg-white border rounded-md dark:bg-slate-800 border-slate-100 dark:border-slate-700"
    >
      <div
        class="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700"
      >
        <h3 class="mb-0 text-lg font-medium text-slate-800 dark:text-slate-100">
          {{ $t('AGENTS_INBOXES_REPORTS.TABLE.HEADER') }}
        </h3>
      </div>

      <woot-loading-state
        v-if="isFetching"
        class="px-4 py-12 text-xs"
        :message="$t('AGENTS_INBOXES_REPORTS.LOADING')"
      />

      <div
        v-else-if="!hasRows"
        class="px-4 py-12 text-sm text-center text-slate-600 dark:text-slate-300"
      >
        {{ $t('AGENTS_INBOXES_REPORTS.NO_RECORDS') }}
      </div>

      <template v-else>
        <div
          v-if="hasHorizontalOverflow && !isScrolledToEnd"
          class="flex items-center justify-end gap-1 px-4 pt-3 text-xs text-slate-500 dark:text-slate-400"
        >
          <span>{{ $t('AGENTS_INBOXES_REPORTS.SCROLL_HINT') }}</span>
          <fluent-icon icon="chevron-right" :size="14" />
        </div>

        <div
          v-else-if="hasHorizontalOverflow"
          class="flex items-center justify-start gap-1 px-4 pt-3 text-xs text-slate-500 dark:text-slate-400"
        >
          <fluent-icon icon="chevron-left" :size="14" />
          <span>{{ $t('AGENTS_INBOXES_REPORTS.SCROLL_HINT_BACK') }}</span>
        </div>

        <div
          ref="tableScroller"
          class="overflow-x-auto"
          @scroll="updateHorizontalScrollState"
        >
          <table class="min-w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th
                  class="sticky left-0 z-10 min-w-[220px] bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600 dark:bg-slate-900 dark:text-slate-300"
                >
                  {{ $t('AGENTS_INBOXES_REPORTS.TABLE.AGENT') }}
                </th>
                <th
                  v-for="inbox in inboxes"
                  :key="inbox.id"
                  :title="inbox.name"
                  class="min-w-[140px] max-w-[220px] bg-slate-50 px-4 py-3 text-right text-xs font-semibold uppercase text-slate-600 dark:bg-slate-900 dark:text-slate-300"
                >
                  <span class="block truncate">{{ inbox.name }}</span>
                </th>
                <th
                  class="min-w-[120px] bg-slate-50 px-4 py-3 text-right text-xs font-semibold uppercase text-slate-600 dark:bg-slate-900 dark:text-slate-300"
                >
                  {{ $t('AGENTS_INBOXES_REPORTS.TABLE.TOTAL') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.agent.id"
                class="border-t border-slate-100 dark:border-slate-700"
              >
                <td
                  class="sticky left-0 z-10 border-t bg-white px-4 py-3 text-left align-middle dark:bg-slate-800 border-slate-100 dark:border-slate-700"
                >
                  <UserAvatarWithName
                    :user="agentCellUser(row.agent)"
                    size="22px"
                    text-class="text-sm font-medium text-slate-800"
                    :show-link="false"
                  />
                </td>
                <td
                  v-for="inbox in inboxes"
                  :key="`${row.agent.id}-${inbox.id}`"
                  class="border-t px-4 py-3 text-right font-mono text-sm tabular-nums text-slate-700 dark:text-slate-200 border-slate-100 dark:border-slate-700"
                >
                  {{ cellValue(row, inbox.id) }}
                </td>
                <td
                  class="border-t px-4 py-3 text-right font-mono text-sm font-semibold tabular-nums text-slate-800 dark:text-slate-100 border-slate-100 dark:border-slate-700"
                >
                  {{ row.total }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td
                  class="sticky left-0 z-10 border-t bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600 dark:bg-slate-900 dark:text-slate-300 border-slate-100 dark:border-slate-700"
                >
                  {{ $t('AGENTS_INBOXES_REPORTS.TABLE.TOTAL') }}
                </td>
                <td
                  v-for="inbox in inboxes"
                  :key="`total-${inbox.id}`"
                  class="border-t bg-slate-50 px-4 py-3 text-right font-mono text-sm font-semibold tabular-nums text-slate-700 dark:bg-slate-900 dark:text-slate-200 border-slate-100 dark:border-slate-700"
                >
                  {{ columnTotals[inbox.id] }}
                </td>
                <td
                  class="border-t bg-slate-50 px-4 py-3 text-right font-mono text-sm font-semibold tabular-nums text-slate-800 dark:bg-slate-900 dark:text-slate-100 border-slate-100 dark:border-slate-700"
                >
                  {{ grandTotal }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </template>
    </section>
  </div>
</template>
